import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const BACKEND_BASE_URL = API_BASE_URL.replace(/\/api$/, '');

// Append JWT token to media URLs so <img>/<video> tags can access authenticated files
export function getAuthenticatedMediaUrl(mediaUrl) {
  if (!mediaUrl) return '';
  const token = localStorage.getItem('token');
  // Build full URL if relative
  let fullUrl = mediaUrl;
  if (mediaUrl.startsWith('/')) {
    fullUrl = BACKEND_BASE_URL + mediaUrl;
  } else if (!mediaUrl.startsWith('http')) {
    fullUrl = BACKEND_BASE_URL + '/' + mediaUrl;
  }
  if (token) {
    const separator = fullUrl.includes('?') ? '&' : '?';
    return fullUrl + separator + 'token=' + encodeURIComponent(token);
  }
  return fullUrl;
}

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token validation helper
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    // Decode JWT token (basic check - the payload is base64 encoded)
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    
    // Check if token is expired
    return payload.exp < currentTime;
  } catch (error) {
    console.error('Token validation error:', error);
    return true; // If we can't decode it, consider it invalid
  }
};

// Clear expired token and redirect to login
const handleExpiredToken = () => {
  console.log('🔒 Token expired or invalid - clearing session and redirecting to login');
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('admin');
  
  // Redirect to login page
  if (window.location.pathname !== '/login' && window.location.pathname !== '/admin/login') {
    window.location.href = '/login';
  }
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    
    if (token) {
      // Check if token is expired before making the request
      if (isTokenExpired(token)) {
        handleExpiredToken();
        return Promise.reject(new Error('Token expired'));
      }
      
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized (token expired/invalid)
    if (error.response?.status === 401) {
      console.log('🔒 Received 401 - Token expired or invalid');
      handleExpiredToken();
    }
    
    // Handle other authentication errors
    if (error.response?.status === 403) {
      console.log('🚫 Access forbidden - insufficient permissions');
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },
  
  adminLogin: async (username, password) => {
    const response = await api.post('/auth/admin/login', { username, password });
    return response.data;
  },
};

// Admin API
export const adminAPI = {
  getStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  },
  
  getCompanies: async () => {
    const response = await api.get('/admin/companies');
    return response.data;
  },
  
  createCompany: async (company) => {
    const response = await api.post('/admin/companies', company);
    return response.data;
  },
  
  updateCompany: async (compID, company) => {
    const response = await api.put(`/admin/companies/${compID}`, company);
    return response.data;
  },
  
  deleteCompany: async (compID) => {
    const response = await api.delete(`/admin/companies/${compID}`);
    return response.data;
  },
  
  getUsers: async () => {
    const response = await api.get('/admin/users');
    return response.data;
  },
  
  createUser: async (user) => {
    const response = await api.post('/admin/users', user);
    return response.data;
  },
  
  updateUser: async (userID, user) => {
    const response = await api.put(`/admin/users/${userID}`, user);
    return response.data;
  },
  
  deleteUser: async (userID) => {
    const response = await api.delete(`/admin/users/${userID}`);
    return response.data;
  },
  
  getLicenses: async () => {
    const response = await api.get('/admin/licenses');
    return response.data;
  },
  
  createLicense: async (license) => {
    const response = await api.post('/admin/licenses', license);
    return response.data;
  },
  
  updateLicense: async (licenseID, license) => {
    const response = await api.put(`/admin/licenses/${licenseID}`, license);
    return response.data;
  },
  
  deleteLicense: async (licenseID) => {
    const response = await api.delete(`/admin/licenses/${licenseID}`);
    return response.data;
  },
};

// Agent Groups API
export const agentGroupsAPI = {
  getAgentGroups: async (compID) => {
    const response = await api.get(`/agent-groups/company/${compID}/agent-groups`);
    return response.data;
  },
  
  createAgentGroup: async (group) => {
    const response = await api.post(`/agent-groups/company/${group.compID}/agent-groups`, group);
    return response.data;
  },
  
  updateAgentGroup: async (agentGroupsID, group) => {
    const response = await api.put(`/agent-groups/company/${group.compID}/agent-groups/${agentGroupsID}`, group);
    return response.data;
  },
  
  deleteAgentGroup: async (compID, agentGroupsID) => {
    const response = await api.delete(`/agent-groups/company/${compID}/agent-groups/${agentGroupsID}`);
    return response.data;
  },
};

// Media API
export const mediaAPI = {
  getAllMedia: async (compID) => {
    const response = await api.get(`/media/company/${compID}/media`);
    return response.data;
  },
  
  uploadMedia: async (compID, mediaGroupID, formData) => {
    // Add mediaGroupID to formData if provided
    if (mediaGroupID && mediaGroupID !== '') {
      formData.append('mediaGroupID', mediaGroupID);
    }
    
    const response = await api.post(`/media/company/${compID}/media`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  
  updateMedia: async (compID, mediaID, media) => {
    const response = await api.put(`/media/company/${compID}/media/${mediaID}`, media);
    return response.data;
  },
  
  deleteMedia: async (compID, mediaID) => {
    const response = await api.delete(`/media/company/${compID}/media/${mediaID}`);
    return response.data;
  },
};

// Media Groups API (separate from media API for better organization)
export const mediaGroupsAPI = {
  getMediaGroups: async (compID) => {
    const response = await api.get(`/media/company/${compID}/media-groups`);
    return response.data;
  },
  
  createMediaGroup: async (compID, group) => {
    const response = await api.post(`/media/company/${compID}/media-groups`, group);
    return response.data;
  },
  
  updateMediaGroup: async (compID, mediaGroupID, group) => {
    const response = await api.put(`/media/company/${compID}/media-groups/${mediaGroupID}`, group);
    return response.data;
  },
  
  deleteMediaGroup: async (compID, mediaGroupID) => {
    const response = await api.delete(`/media/company/${compID}/media-groups/${mediaGroupID}`);
    return response.data;
  },
  
  getMediaInGroup: async (compID, mediaGroupID) => {
    const response = await api.get(`/media/company/${compID}/media-groups/${mediaGroupID}/media`);
    return response.data;
  },
  
  generatePublicLink: async (compID, mediaGroupID) => {
    const response = await api.post(`/media/company/${compID}/media-groups/${mediaGroupID}/generate-public-link`);
    return response.data;
  },
  
  disablePublicLink: async (compID, mediaGroupID) => {
    const response = await api.delete(`/media/company/${compID}/media-groups/${mediaGroupID}/public-link`);
    return response.data;
  },
};

// Agents API
export const agentsAPI = {
  getAgents: async (compID) => {
    const response = await api.get(`/agents/company/${compID}/agents`);
    return response.data;
  },
  
  createAgent: async (agent) => {
    const response = await api.post(`/agents/company/${agent.compID}/agents`, agent);
    return response.data;
  },
  
  updateAgent: async (agentID, agent) => {
    const response = await api.put(`/agents/company/${agent.compID}/agents/${agentID}`, agent);
    return response.data;
  },
  
  deleteAgent: async (compID, agentID) => {
    const response = await api.delete(`/agents/company/${compID}/agents/${agentID}`);
    return response.data;
  },
  
  getAgentMediaStatus: async (compID, agentID) => {
    const response = await api.get(`/agents/company/${compID}/agents/${agentID}/media-status`);
    return response.data;
  },
  
  bulkUpdateAgents: async (compID, agentIDs, mediaGroupID) => {
    const response = await api.put(`/agents/company/${compID}/agents/bulk-update`, {
      agentIDs,
      mediaGroupID
    });
    return response.data;
  },

  getLicenseStatus: async (compID) => {
    const response = await api.get(`/agents/company/${compID}/license-status`);
    return response.data;
  },
};

// Settings API
export const settingsAPI = {
  getAll: async () => {
    const response = await api.get('/admin/settings');
    return response.data;
  },

  update: async (settings) => {
    const response = await api.put('/admin/settings', settings);
    return response.data;
  },

  getByCategory: async (category) => {
    const response = await api.get(`/admin/settings/${category}`);
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await api.post('/admin/change-password', {
      currentPassword,
      newPassword
    });
    return response.data;
  }
};

// Client Licenses API
export const clientLicensesAPI = {
  getLicenses: async (compID) => {
    const response = await api.get(`/licenses/company/${compID}/licenses`);
    return response.data;
  },

  requestLicense: async (compID, requestData) => {
    const response = await api.post(`/licenses/company/${compID}/license-request`, requestData);
    return response.data;
  }
};

// Publishing API - for content distribution
export const publishingAPI = {
  // Publish content to agent groups
  createPublishingTaskByGroup: async (compID, agentGroupIDs, mediaGroupID, scheduledTime = null) => {
    const response = await api.post('/publishing/tasks/by-group', {
      compID,
      agentGroupIDs,
      mediaGroupID,
      scheduledTime,
      status: 'pending'
    });
    return response.data;
  },

  // Publish content to individual agents
  createPublishingTaskByAgent: async (compID, agentIDs, mediaGroupID, scheduledTime = null) => {
    const response = await api.post('/publishing/tasks/by-agent', {
      compID,
      agentIDs,
      mediaGroupID,
      scheduledTime,
      status: 'pending'
    });
    return response.data;
  },

  // Get publishing tasks for a company
  getPublishingTasks: async (compID, page = 1, limit = 20, status = null) => {
    const params = { 
      compID,
      page,
      limit
    };
    if (status) params.status = status;
    const response = await api.get('/publishing/tasks', { params });
    return response.data;
  },

  // Get publishing status for agents
  getPublishingStatus: async (compID, taskID = null) => {
    const params = { compID };
    if (taskID) params.taskID = taskID;
    const response = await api.get('/publishing/status', { params });
    return response.data;
  },

  // Update publishing task status
  updateTaskStatus: async (compID, taskID, status, message = null) => {
    const response = await api.put(`/publishing/tasks/${taskID}`, {
      compID,
      status,
      message,
      updatedAt: new Date().toISOString()
    });
    return response.data;
  },

  // Cancel publishing task
  cancelTask: async (compID, taskID) => {
    const response = await api.delete(`/publishing/tasks/${taskID}`, {
      params: { compID }
    });
    return response.data;
  },

  // Get file-level publishing status
  getFileStatus: async (compID, taskID = null, agentID = null) => {
    const params = { compID };
    if (taskID) params.taskID = taskID;
    if (agentID) params.agentID = agentID;
    const response = await api.get('/publishing/file-status', { params });
    return response.data;
  },

  // Get agent assignments (what each agent should be displaying)
  getAgentAssignments: async (compID, agentID = null) => {
    const params = { compID };
    if (agentID) params.agentID = agentID;
    const response = await api.get('/publishing/assignments', { params });
    return response.data;
  }
};

// Utility functions for token management
export const tokenUtils = {
  // Check if current token is valid
  isValidToken: () => {
    const token = localStorage.getItem('token');
    return token && !isTokenExpired(token);
  },
  
  // Get token expiration time
  getTokenExpiration: () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return new Date(payload.exp * 1000);
    } catch (error) {
      return null;
    }
  },
  
  // Check and handle expired token on app startup
  validateTokenOnStartup: () => {
    const token = localStorage.getItem('token');
    
    if (token && isTokenExpired(token)) {
      console.log('🔒 Found expired token on startup - cleaning up');
      handleExpiredToken();
      return false;
    }
    
    if (token) {
      const expiration = tokenUtils.getTokenExpiration();
      console.log('✅ Valid token found, expires at:', expiration);
      return true;
    }
    
    console.log('ℹ️ No token found');
    return false;
  },
  
  // Manually clear token and redirect
  logout: () => {
    console.log('🚪 Manual logout - clearing session');
    handleExpiredToken();
  }
};

export default api;
